import { useAsyncCallback } from '@pique/core/hooks/pique-async-hooks';
import { useDocMetaHelper } from '@pique/core/hooks/use-block-suite-page-meta';
import { useDocCollectionHelper } from '@pique/core/hooks/use-block-suite-workspace-helper';
import { CollectionService } from '@pique/core/modules/collection';
import {
  TranscriptionService,
  WorkspacePropertiesAdapter,
} from '@pique/core/modules/properties';
import { resolvePersistentWorkbenchDocMode } from '@pique/core/modules/workbench/entities/workbench-layout-policy';
import { DocsService, openDoc, useService } from '@toeverything/infra';
import { useCallback } from 'react';
import { applyUpdate, encodeStateAsUpdate } from 'yjs';

import type { DocCollection } from '../../shared';
import { useNavigateHelper } from '../use-navigate-helper';

export function useBlockSuiteMetaHelper(docCollection: DocCollection) {
  const { setDocMeta, getDocMeta, setDocReadonly, setDocTitle } =
    useDocMetaHelper(docCollection);
  const { createDoc } = useDocCollectionHelper(docCollection);
  const { openPage } = useNavigateHelper();
  const collectionService = useService(CollectionService);
  const pageRecordList = useService(DocsService).list;
  const propertiesAdapter = useService(WorkspacePropertiesAdapter);
  const transcriptionService = useService(TranscriptionService);

  // TODO-Doma
  // "Remove" may cause ambiguity here. Consider renaming as "moveToTrash".
  const removeToTrash = useCallback(
    (pageId: string) => {
      setDocMeta(pageId, {
        trash: true,
        trashDate: Date.now(),
      });
      setDocReadonly(pageId, true);
      collectionService.deletePagesFromCollections([pageId]);
    },
    [collectionService, setDocMeta, setDocReadonly]
  );

  const restoreFromTrash = useCallback(
    (pageId: string) => {
      setDocMeta(pageId, {
        trash: false,
        trashDate: undefined,
      });
      setDocReadonly(pageId, false);
    },
    [setDocMeta, setDocReadonly]
  );

  const permanentlyDeletePage = useCallback(
    (pageId: string) => {
      docCollection.removeDoc(pageId);
    },
    [docCollection]
  );

  /**
   * see {@link useBlockSuiteWorkspacePageIsPublic}
   */
  const publicPage = useCallback(
    (pageId: string) => {
      setDocMeta(pageId, {
        isPublic: true,
      });
    },
    [setDocMeta]
  );

  /**
   * see {@link useBlockSuiteWorkspacePageIsPublic}
   */
  const cancelPublicPage = useCallback(
    (pageId: string) => {
      setDocMeta(pageId, {
        isPublic: false,
      });
    },
    [setDocMeta]
  );

  const duplicate = useAsyncCallback(
    async (pageId: string, openPageAfterDuplication: boolean = true) => {
      const currentPagePrimaryMode =
        pageRecordList.doc$(pageId).value?.primaryMode$.value;
      const currentPageMeta = getDocMeta(pageId);
      const currentPage = openDoc(docCollection, pageId);

      if (!currentPageMeta || !currentPage) {
        return;
      }

      const newPage = createDoc();
      setDocMeta(newPage.id, {
        tags: currentPageMeta.tags,
      });
      const update = encodeStateAsUpdate(currentPage.spaceDoc);
      applyUpdate(newPage.spaceDoc, update);

      const lastDigitRegex = /\((\d+)\)$/;
      const match = currentPageMeta.title.match(lastDigitRegex);
      const matchedDigit = match?.[1];
      const newNumber = matchedDigit ? parseInt(matchedDigit, 10) + 1 : 1;

      const newPageTitle =
        currentPageMeta.title.replace(lastDigitRegex, '') + `(${newNumber})`;

      pageRecordList
        .doc$(newPage.id)
        .value?.setPrimaryMode(
          resolvePersistentWorkbenchDocMode(currentPagePrimaryMode || 'page')
        );

      // Copy over workspace page properties (system + custom) from the source doc
      try {
        propertiesAdapter.ensurePageProperties(pageId);
        propertiesAdapter.ensurePageProperties(newPage.id);

        // Compute copies inside a single Yjs transaction for consistency
        propertiesAdapter.transact(() => {
          // ---- System properties ----
          // Action Items: rewrite pageId/pageTitle to the duplicated doc
          const srcItems = propertiesAdapter.getPageActionItems(pageId) || [];
          if (Array.isArray(srcItems) && srcItems.length > 0) {
            const copied = srcItems.map(item => ({
              ...item,
              id: crypto.randomUUID(),
              pageId: newPage.id,
              pageTitle: newPageTitle,
            }));
            propertiesAdapter.setPageActionItems(newPage.id, copied);
          }

          // Attendees
          const attendees = propertiesAdapter.getPageAttendees(pageId) || [];
          if (Array.isArray(attendees) && attendees.length > 0) {
            propertiesAdapter.setPageAttendees(
              newPage.id,
              attendees.map(a => ({ ...a }))
            );
          }

          // Follow-up email status
          // const emailStatus = propertiesAdapter.getPageFollowupEmailStatus(pageId);
          // if (emailStatus) {
          //   propertiesAdapter.setPageFollowupEmailStatus(newPage.id, emailStatus);
          // }

          // ---- Custom properties ----
          const srcProps = propertiesAdapter.getPageProperties(pageId);
          const dstProps = propertiesAdapter.getPageProperties(newPage.id);
          if (srcProps && dstProps) {
            // Copy only properties that still exist in the workspace schema
            const customEntries = Object.entries(srcProps.custom || {});
            for (const [propId, prop] of customEntries) {
              // Shallow clone is sufficient; values are primitives/arrays/objects used as-is
              (dstProps.custom as Record<string, unknown>)[propId] = {
                ...(prop as object),
              };
            }
          }
        });

        // Copy transcription and summary (blob storage)
        await transcriptionService.ensurePageLoaded(pageId);
        const transcription =
          transcriptionService.getPageTranscription(pageId) || [];
        if (Array.isArray(transcription) && transcription.length > 0) {
          transcriptionService.setPageTranscription(
            newPage.id,
            transcription.map(c => ({ ...c }))
          );
        }

        const summary = transcriptionService.getPageSummary(pageId) || [];
        if (Array.isArray(summary) && summary.length > 0) {
          transcriptionService.setPageSummary(
            newPage.id,
            summary.map(s => ({ ...s }))
          );
        }
      } catch (err) {
        // Non-fatal: duplicating properties should not block content duplication
        console.warn('Failed to duplicate page properties:', err);
      }

      // Finally set the duplicated doc title
      setDocTitle(newPage.id, newPageTitle);
      openPageAfterDuplication && openPage(docCollection.id, newPage.id);
    },
    [
      docCollection,
      createDoc,
      getDocMeta,
      openPage,
      pageRecordList,
      setDocMeta,
      setDocTitle,
      propertiesAdapter,
      transcriptionService,
    ]
  );

  return {
    publicPage,
    cancelPublicPage,

    removeToTrash,
    restoreFromTrash,
    permanentlyDeletePage,

    duplicate,
  };
}
